import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import {
  type MemberStore,
  type UpdateProfileParams,
} from '@/features/iam/members/MemberStore'
import { type Member, type MemberMetadata } from '@/features/iam/members/Member'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { deepEqual } from '@/utils/object-util'
import { func, pattern, required, validationContext } from 'validx'
import { SelectionViewModel } from '@/features/iam/users/SelectionViewModel'
import { type SkillStore } from '@/features/iam/skills/SkillStore'
import { type Skill } from '@/features/iam/skills/Skill'
import { type RoleStore } from '@/features/iam/roles/RoleStore'
import { type Role } from '@/features/iam/roles/Role'
import { CursorPaged } from '@/infra/paging'
import { Id } from '@/infra/id'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ActivationLifecycle } from '@/infra/activation'
import { isRefreshDesign, setDesign } from '@/infra/design'
import { ignoreAbortError } from '@/utils/abort-util'

const skillSorter = (a: Skill, b: Skill) => {
  const aName = a.name.toLowerCase()
  const bName = b.name.toLowerCase()
  if (aName > bName) return 1
  if (aName < bName) return -1
  return 0
}

/**
 * View model for the General tab.
 */
export class ProfileGeneralViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  @observable accessor givenName = ''
  @observable accessor familyName = ''
  @observable accessor email = ''
  @observable accessor phone = ''
  @observable accessor memberMetadata: MemberMetadata = {}

  readonly skillSelection: SelectionViewModel<Skill>
  readonly roleSelection: SelectionViewModel<Role>
  readonly validation

  constructor(
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _member: Member,
    private readonly _memberStore: MemberStore,
    private readonly _skillStore: SkillStore,
    private readonly _roleStore: RoleStore,
  ) {
    this.skillSelection = new SelectionViewModel<Skill>({
      createAlertErrorHandler: this._createAlertErrorHandler,
      createAlertRetryWrapper: this._createAlertRetryWrapper,
      getItemId: (skill) => Id.assert(skill.id),
      canUpdate: () => true,
      fetch: (query, signal) =>
        this._skillStore.listSkills(
          {
            workspaceId: this._member.workspace.id,
            after: query.after,
            limit: query.limit,
          },
          signal,
        ),
      selection: () => this._member.skills.slice().sort(skillSorter),
      add: (skill) => this._memberStore.addSkill(this._member, skill),
      remove: (skill) => this._memberStore.removeSkill(this._member, skill),
      getTextSearchValues: (skill) => [skill.name, skill.description],
    })

    this.roleSelection = new SelectionViewModel<Role>({
      createAlertErrorHandler: this._createAlertErrorHandler,
      createAlertRetryWrapper: this._createAlertRetryWrapper,
      getItemId: (role) => Id.assert(role.id),
      // Can't change owner role for yourself.
      canUpdate: (role) =>
        role.isOwnerRole === false || !this._member.isCurrentUser,
      fetch: async (_query, signal) =>
        CursorPaged.of(
          await this._roleStore.fetchRoles(
            { workspaceId: this._member.workspace.id },
            signal,
          ),
        ),
      selection: () => this._member.roles,
      add: (role) =>
        this._memberStore.addRole(
          this._member.workspace.id,
          this._member.user.publicId,
          role.id,
        ),
      remove: (role) => this._memberStore.removeRole(this._member, role),
      getTextSearchValues: (role) => [role.name, role.description],
    })

    this._lifecycle.manage(this.skillSelection).manage(this.roleSelection)

    this.validation = validationContext(this, {
      givenName: [
        required(),
        func(({ value }: { value: string }) => {
          return (
            value.length <= 40 || "First name can't be more than 40 characters"
          )
        }),
      ],
      familyName: [
        required(),
        func(({ value }: { value: string }) => {
          return (
            value.length <= 40 || "Last name can't be more than 40 characters"
          )
        }),
      ],
      email: [
        required(),
        pattern('email'),
        func(({ value }: { value: string }) => {
          return (
            value.length <= 320 || "Email can't be more than 320 characters"
          )
        }),
      ],
      phone: [
        func(
          (ctx) =>
            // Only run the phone number rule if we have a value,
            // as phone numbers are optional.
            !ctx.value ||
            pattern(
              // Super basic regex for phone number chars.
              /^[+]?[ 0-9()-]+$/i,
              'This does not look like a phone number.',
            )(ctx),
        ),
        func(({ value }: { value: string }) => {
          return value.length <= 20 || "Phone can't be more than 20 characters"
        }),
      ],
    })
  }

  @computed
  get inputFields() {
    return this._member.workspace.memberMetadataFields.filter(
      (field) => field.active && field.permissions.includes('USER_READ'),
    )
  }

  /**
   * Whether we can save.
   */
  @computed
  get canSave() {
    return !this.saveProfile.pending && this.profileHasChanged && this.canEdit
  }

  @computed
  get canEdit() {
    return !this._member.workspace.config.signup.externalProfiles
  }

  /**
   * Whether the profile has changed.
   */
  @computed
  get profileHasChanged() {
    return !deepEqual(
      this._collectParamsFromMember(),
      this._collectParamsForUpdate(),
    )
  }

  /**
   * Whether this browser uses the refreshed design.
   */
  get isRefreshDesign() {
    return isRefreshDesign
  }

  /**
   * Switches this browser between the classic and refreshed design.
   */
  @action.bound
  toggleDesign() {
    setDesign(isRefreshDesign ? 'classic' : 'refresh')
  }

  activate() {
    this._lifecycle.activate()
    void this.fetch(this._lifecycle.signal).catch(ignoreAbortError())
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Loads the member.
   */
  fetch = task(async (signal?: AbortSignal) => {
    await this._memberStore.fetchMember(
      {
        workspaceId: this._member.workspace.id,
        userId: this._member.user.id,
        userPublicId: this._member.user.publicId,
      },
      signal,
    )
    this._initializeFromMember()
  })

  /**
   * Saves the profile.
   */
  saveProfile = task.resolved(async () => {
    if (!this.profileHasChanged) {
      return
    }

    if (!this.validation.reset().validate().isValid) {
      return
    }

    try {
      await this._memberStore
        .updateProfile(this._member, this._collectParamsForUpdate())
        .catch(this._createAlertErrorHandler('Saving profile failed'))
    } catch {
      return
    }
    // Reload from the member.
    this._initializeFromMember()
    this._newAlert()
      .withState('success')
      .saying('Success!', 'Your profile has been updated.')
      .autoDismiss()
      .show()
  })

  @action.bound
  onMetadataChange(fieldId: string, value: string | number | boolean | null) {
    this.memberMetadata[fieldId] = value
  }

  /**
   * Collects params for updating.
   */
  private _collectParamsForUpdate(): UpdateProfileParams {
    return {
      givenName: this.givenName,
      familyName: this.familyName,
      email: this.email,
      phone: this.phone,
      memberMetadata: this.memberMetadata,
    }
  }

  /**
   * Collects params from the member. Used to compare with entered values in order to determine if
   * there has been any changes.
   */
  private _collectParamsFromMember(): UpdateProfileParams {
    return {
      givenName: this._member.givenName,
      familyName: this._member.familyName,
      email: this._member.email,
      phone: this._member.phone,
      memberMetadata: this._member.memberMetadata,
    }
  }

  /**
   * Initializes fields from the member.
   */
  @action.bound
  private _initializeFromMember() {
    this.givenName = this._member.givenName
    this.familyName = this._member.familyName
    this.email = this._member.email
    this.phone = this._member.phone
    this.memberMetadata = {
      ...this._member.memberMetadata,
    }
  }
}
